/* eslint-disable complexity */
import React, { JSX } from 'react';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { Grid } from '../../../common/components/Grid/Grid';
import { GetResultDto } from '../../../common/spdCore/autogenerated/spdApiClient';
import { GivingExamMode } from '../../../common/spdCore/validation/schemas';
import { HearingChart } from '../../../common/components/Charts/HearingChart';
import { ECGPreviewButton } from '../ECGResults/ECGPreviewButton';

interface ResultsProps {
  data: GetResultDto[];
}

export const Results: React.FC<ResultsProps> = props => {
  const columns = [
    { header: 'Numer zadania', property: 'taskNumber' },
    { header: 'Całkowity czas prezentacji', property: 'taskTime' },
    { header: 'Czas latencji (ostatni)', property: 'latencyTime' },
    { header: 'Czas decyzji (ostatni)', property: 'decisionTime' },
    { header: 'Czas reakcji (ostatni)', property: 'reactionTime' },
    { header: 'Odpowiedź (ostatnia)', property: 'codeAnswer' },
    { header: 'Język', property: 'lange' },
    { header: 'Pytanie', property: 'taskText' },
    { header: 'Zdekodowana odpowiedź (ostatnia)', property: 'value' },
    { header: 'Liczba odpowiedzi', property: 'reactionsCount' },
    { header: '', property: 'psychologistEdit' },
  ];

  const data = props.data.map(result => {
    if (result.reactions && result.reactions?.length > 0) {
      const lastReaction = result.reactions.slice(-1)[0];
      const lastAnswer = lastReaction?.answers?.slice(-1)[0];
      const isECGResult = lastAnswer?.valueType === 'resultsECG';
      return {
        key: result.id,
        values: {
          taskNumber: result.taskNumber,
          taskText: result.taskText,
          taskTime: result.taskTime,
          latencyTime: historicalView(
            result.reactions?.length,
            result.reactions.map(r => r?.latencyTime).join('; '),
            lastReaction.latencyTime,
          ),
          decisionTime: historicalView(
            result.reactions?.length,
            result.reactions.map(r => r?.decisionTime).join('; '),
            lastReaction.decisionTime,
          ),
          reactionTime: historicalView(
            result.reactions?.length,
            result.reactions.map(r => r?.reactionTime).join('; '),
            lastReaction.reactionTime,
          ),
          codeAnswer: historicalView(
            result.reactions?.length,
            result.reactions
              .map(r => r?.answers?.map(o => o.codeAnswer).join(', '))
              .join('; '),
            lastReaction?.answers?.map(o => o.codeAnswer).join(', '),
          ),
          lange: lastReaction?.answers
            ?.map(o => o.lange)
            .filter(Boolean)
            .join(', '),
          value: historicalView(
            result.reactions?.length,
            result.reactions
              .map(r => r?.answers?.map(o => o.value).join(', '))
              .join('; '),
            lastReaction?.answers?.map(o => o.value).join(', '),
            false,
            lastReaction?.answers
              ?.filter(o => o.criticalType)
              .map(o => o.criticalType)
              .join(', '),
            lastReaction?.answers?.slice(-1)[0].valueType,
          ),
          reactionsCount: result.reactions?.length,
          psychologistEdit: (
            <>
              {(lastReaction.modeSurvey === GivingExamMode.PsychologistFast ||
                lastReaction.modeSurvey ===
                  GivingExamMode.PsychologistFull) && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Dane zmienione przez psychologa</Tooltip>}
                >
                  <Badge bg="success" className="me-1">
                    *
                  </Badge>
                </OverlayTrigger>
              )}
              {isECGResult && lastAnswer?.value && (
                <ECGPreviewButton data={lastAnswer.value} />
              )}
            </>
          ),
        },
        orderByValues: {
          taskNumber: result.taskNumber || '',
        },
      };
    }

    return {
      key: result.id,
      values: {
        taskNumber: result.taskNumber,
        taskTime: result.taskTime,
        latencyTime: '',
        decisionTime: '',
        reactionTime: '',
        codeAnswer: '',
        lange: '',
        value: '',
        reactionsCount: result.reactions?.length,
        psychologistEdit: '',
      },
      orderByValues: {
        taskNumber: result.taskNumber || '',
      },
    };
  });

  return (
    <Grid
      data={data}
      columns={columns}
      showFilter={true}
      initialOrderColumn="result.taskNumber"
      initialOrderDirection="ASC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage="Brak wyników w teście w badaniu"
    />
  );
};
export function historicalView(
  length: number,
  paramTooltip: string,
  paramBadge: string | number | null | undefined,
  withBadge = true,
  criticalType = '',
  valueType?: string | null,
): string | number | JSX.Element | null | undefined {
  let paramBadgeString: string | number | null | undefined | JSX.Element = (
    paramBadge || ''
  )
    ?.toString()
    .includes(';base64')
    ? 'obraz/video'
    : paramBadge;

  if (valueType === 'rawECG' || valueType === 'resultsECG') {
    paramBadgeString = 'EKG';
  }
  if (valueType === 'stetho') {
    paramBadgeString = 'stetoskop';
  }
  if (valueType === 'audiometry') {
    const pomValue = paramBadge ? JSON.parse(paramBadge.toString()) : null;
    paramBadgeString = `dB: ${pomValue?.dbSPL}, Częstotliwość: ${pomValue?.frequency}, Ucho: ${
      pomValue?.ear === -1 ? 'LEWE' : pomValue?.ear === 1 ? 'PRAWE' : 'OBA'
    }`;
  }
  if (valueType?.toLowerCase() === 'spiro') {
    const spiro = paramBadge ? JSON.parse(paramBadge.toString()) : null;
    paramBadgeString = spiro
      ? `FVC: ${spiro.FVC_percent?.toFixed(1)}%, FEV1: ${spiro.FEV1_percent?.toFixed(1)}%, PEF: ${spiro.PEF_percent?.toFixed(1)}%, FEV1/FVC: ${spiro.FEV1_FVC?.toFixed(1)}%${spiro.norm === false ? ' [poza normą]' : ''}`
      : '';
  }
  if (valueType === 'harmfulFactorList') {
    try {
      const nums = JSON.parse((paramBadge || '').toString());
      paramBadgeString = Array.isArray(nums) ? nums.join(', ') : paramBadge;
    } catch {
      paramBadgeString = paramBadge;
    }
  }
  if (valueType === 'audiometryList') {
    paramBadgeString = (
      <HearingChart data={JSON.parse(paramBadge?.toString() || '[]')} />
    );
  }
  if (valueType === 'webm' && typeof paramBadge === 'string') {
    paramBadgeString = (
      <div>
        <video controls muted src={paramBadge}></video>
      </div>
    );
  }
  if (
    (paramBadge || '')?.toString().startsWith('data:image/png;') &&
    typeof paramBadge === 'string'
  ) {
    paramBadgeString = (
      <div>
        <img src={paramBadge} alt="Image" className="image-style" />
      </div>
    );
  }

  return (
    <>
      {length <= 1 ? (
        <>
          {criticalType && (
            <Badge
              bg="danger"
              className="me-1"
              onClick={e => e.stopPropagation()}
            >
              !!!
            </Badge>
          )}
          {paramBadgeString}
        </>
      ) : (
        <OverlayTrigger
          placement="bottom"
          overlay={<Tooltip>{paramTooltip}</Tooltip>}
        >
          <div>
            {criticalType && (
              <Badge
                bg="danger"
                className="me-1"
                onClick={e => e.stopPropagation()}
              >
                !!!
              </Badge>
            )}
            {withBadge && (
              <Badge
                bg="success"
                className="me-1"
                onClick={e => e.stopPropagation()}
              >
                {paramBadge}
              </Badge>
            )}
            {!withBadge && paramBadgeString}
          </div>
        </OverlayTrigger>
      )}
    </>
  );
}
